import { apiClient } from './apiClient';

export type ServiceStatus = 'healthy' | 'degraded' | 'down' | 'disabled';
export type AlertType = 'warning' | 'error' | 'info';

export interface SystemAlert {
  id: string;
  type: AlertType;
  message: string;
  service?: string;
  dismissible?: boolean;
}

export interface SystemStatus {
  alerts: SystemAlert[];
  services: {
    database: ServiceStatus;
    ai: ServiceStatus;
    email: ServiceStatus;
    puppeteer: ServiceStatus;
    lighthouse: ServiceStatus;
  };
  timestamp?: string;
}

class SystemStatusService {
  private cachedStatus: SystemStatus | null = null;
  private lastFetch: number = 0;
  private readonly CACHE_TTL = 30000; // 30 seconds cache

  /**
   * Get current system status from backend
   * Includes alerts and service health
   */
  async getSystemStatus(): Promise<SystemStatus> {
    const now = Date.now();

    // Return cached status if within TTL
    if (this.cachedStatus && now - this.lastFetch < this.CACHE_TTL) {
      return this.cachedStatus;
    }

    try {
      // apiClient.get returns response.data directly, which is the SystemStatus object
      const status = await apiClient.get<SystemStatus>('/system/status') as unknown as SystemStatus;
      this.cachedStatus = status;
      this.lastFetch = now;
      return status;
    } catch (error) {
      console.error('Failed to fetch system status:', error);

      // If we have cached status, return it even if stale
      if (this.cachedStatus) {
        return this.cachedStatus;
      }

      // Return default healthy status if no cached data
      return {
        alerts: [],
        services: {
          database: 'healthy',
          ai: 'healthy',
          email: 'healthy',
          puppeteer: 'healthy',
          lighthouse: 'healthy',
        },
      };
    }
  }

  /**
   * Force refresh of system status (bypass cache)
   */
  async forceRefresh(): Promise<SystemStatus> {
    this.cachedStatus = null;
    this.lastFetch = 0;
    return this.getSystemStatus();
  }

  /**
   * Check if any service is currently down
   */
  isAnyServiceDown(status: SystemStatus): boolean {
    return Object.values(status.services).some((s) => s === 'down');
  }

  /**
   * Check if any service is degraded
   */
  isAnyServiceDegraded(status: SystemStatus): boolean {
    return Object.values(status.services).some((s) => s === 'degraded');
  }

  /**
   * Get human-readable service name
   */
  getServiceDisplayName(service: string): string {
    const names: Record<string, string> = {
      database: 'Database',
      ai: 'AI Services',
      email: 'Email Service',
      puppeteer: 'Web Scraping',
      lighthouse: 'Performance Analysis',
    };
    return names[service] || service;
  }
}

export const systemStatusService = new SystemStatusService();
export default systemStatusService;
